import React from 'react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line no-restricted-imports
import { ChatTableResponseBody } from '../types/alpha.types';

/**
 * Generic renderer for any Conv-AI answer that arrives as `type: "table"`.
 *
 * Deliberately knows nothing about trips. The backend returns positional rows
 * under dynamic column labels, so the same component already covers trip lists,
 * run lists, vehicle lists, KPI comparisons and exception lists — anything the
 * conversational collection operations can project.
 *
 * Why not shared/components/DataTable or FullWidthTable: both take row OBJECTS
 * keyed by a `field` per column and require `T extends { id }`, and both bundle
 * client-side paging/sorting/search. Conv-AI sends `unknown[][]` with labels
 * decided per answer, and paging and sorting are CONVERSATIONAL ("sort them by
 * pickup time", "show next 10") — owning them client-side would fight the
 * backend contract. Their styling conventions are reused here instead.
 */

/** Shown in place of a null/undefined/blank cell so columns stay readable. */
const EMPTY_CELL = '—';

/**
 * How long a cell may be before it is allowed to WRAP.
 *
 * Cells are `whitespace-nowrap` so a timestamp or a passenger name keeps its
 * shape and the columns line up. That is right until a cell holds free text —
 * a stop note now arrives as a table cell (Trip Comments / Stop Notes,
 * 2026-09-18) — where one long line pushes the table far wider than the chat
 * panel and leaves the reader scrolling sideways through a sentence.
 *
 * Above this length the cell wraps inside a bounded width instead. Short cells
 * are untouched, so nothing that reads well today changes.
 */
const WRAP_CELL_CHARS = 48;

/** Props are the response body minus its discriminator — one source of truth. */
export type ChatTableResponseProps = Omit<ChatTableResponseBody, 'type'>;

/** A row the backend sent as something other than an array still renders. */
const toCells = (row: unknown): unknown[] => (Array.isArray(row) ? row : [row]);

/**
 * Renders one cell value as plain text.
 *
 * Every value leaves here as a string and is handed to React as a text child,
 * never as markup — which is why this component needs no sanitiser and has no
 * dangerouslySetInnerHTML. A value like `<img onerror=...>` coming back from a
 * query is escaped by React and displayed literally.
 */
const formatCell = (value: unknown): string => {
  if (value === null || value === undefined) return EMPTY_CELL;
  if (typeof value === 'string') return value.trim() === '' ? EMPTY_CELL : value;
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : EMPTY_CELL;
  if (typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value) ?? EMPTY_CELL;
  } catch {
    // Circular or otherwise unserialisable — never worth breaking the message.
    return EMPTY_CELL;
  }
};

const ChatTableResponse: React.FC<ChatTableResponseProps> = ({ title, columns, rows, summary }) => {
  const { t } = useTranslation();

  // The backend's own sentence about the result (e.g. that it is capped).
  // Shown as-is: whether 50 visible rows are the whole answer is something
  // only the backend knows, so nothing here infers or rewords it.
  const summaryText = typeof summary === 'string' ? summary.trim() : '';

  const safeColumns = Array.isArray(columns) ? columns : [];
  const safeRows = Array.isArray(rows) ? rows : [];

  // Widen to the longest row so a backend that sends more cells than headers
  // still shows every value; short rows pad with the placeholder. Order is
  // never re-derived — column index c always maps to cell index c.
  const columnCount = safeRows.reduce(
    (widest, row) => Math.max(widest, toCells(row).length),
    safeColumns.length,
  );

  const columnIndexes = Array.from({ length: columnCount }, (_, index) => index);

  if (safeRows.length === 0 || columnCount === 0) {
    return (
      <div className="w-full min-w-0">
        {title && <p className="mb-1 text-sm font-semibold text-text">{title}</p>}
        <p className="text-sm text-text-muted">{t('alpha.table.noResults')}</p>
        {summaryText && (
          <p data-testid="chat-table-summary" className="mt-1 text-xs text-text-muted">
            {summaryText}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="w-full min-w-0">
      {/* Title sits outside the scroll container so it stays put while scrolling. */}
      {title && <p className="mb-2 text-sm font-semibold text-text">{title}</p>}

      {/* max-w-full + min-w-0 keep the table inside the chat panel. */}
      <div
        role="region"
        aria-label={title || t('alpha.table.label')}
        // A scrollable region must be reachable by keyboard (WCAG 2.1.1), but
        // jsx-a11y only whitelists role="tabpanel" for tabIndex.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        className="max-w-full min-w-0 overflow-x-auto rounded-lg border border-border bg-surface"
      >
        {/*
          w-max sizes the table to its content so columns are never crushed;
          min-w-full stretches it to fill the bubble when the content is narrow.
          Together with the wrapper's overflow-x-auto that gives horizontal
          scrolling on narrow panels instead of unreadable squeezed columns.
        */}
        <table className="w-max min-w-full border-collapse text-sm">
          <thead className="border-b border-border bg-surface-secondary">
            <tr>
              {columnIndexes.map((colIdx) => (
                <th
                  key={colIdx}
                  scope="col"
                  className="whitespace-nowrap px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-text-muted"
                >
                  {safeColumns[colIdx] ?? ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {safeRows.map((row, rowIdx) => {
              const cells = toCells(row);
              return (
                <tr
                  key={rowIdx}
                  className="transition-colors hover:bg-surface-secondary/60"
                >
                  {columnIndexes.map((colIdx) => {
                    const text = formatCell(cells[colIdx]);
                    const wraps = text.length > WRAP_CELL_CHARS;
                    return (
                      <td
                        key={colIdx}
                        className={`px-3 py-2 align-top text-text ${
                          wraps
                            ? 'max-w-[15rem] whitespace-normal break-words'
                            : 'whitespace-nowrap'
                        }`}
                      >
                        {text}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/*
        Only when the backend sent no title: its titles already read
        "Total: N record(s)", and showing both reads as a duplicate.
      */}
      {!title && (
        <p className="mt-1 text-xs text-text-muted">
          {t('alpha.table.recordCount', { count: safeRows.length })}
        </p>
      )}

      {/*
        The backend's summary of the result — in particular a capped list's
        "Showing 50 rows. The source reported 57 in total…". Subordinate to
        the table, but always visible: without it 50 rows read as complete.
      */}
      {summaryText && (
        <p data-testid="chat-table-summary" className="mt-1 text-xs text-text-muted">
          {summaryText}
        </p>
      )}
    </div>
  );
};

export default ChatTableResponse;
